import React, { useEffect, useMemo, useState } from "react";
import { requireAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import { BellOff, Bell } from "lucide-react";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "hooks/crm/useNotifications";

const CHANNEL_LABELS = { email: "Email", inApp: "In-app" };
const CHANNEL_ORDER = ["email", "inApp"];

function Switch({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-brand" : "bg-muted"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function NotificationPreferences() {
  const { toast } = useToast();
  const { data, isLoading, isError } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();

  // draft = explicit per-user overrides only ({ [eventKey]: { email?, inApp? } });
  // events not in draft follow their default. Seeded from the API's userValues.
  const [draft, setDraft] = useState({});
  const [mutedAll, setMutedAll] = useState(false);
  const [snapshot, setSnapshot] = useState("");

  useEffect(() => {
    if (!data) return;
    const seed = {};
    (data.events || []).forEach((ev) => {
      const o = {};
      (ev.channels || []).forEach((ch) => {
        if (ch.userValue != null) o[ch.channel] = ch.userValue;
      });
      if (Object.keys(o).length) seed[ev.eventKey] = o;
    });
    setDraft(seed);
    setMutedAll(!!data.mutedAll);
    setSnapshot(JSON.stringify({ seed, mutedAll: !!data.mutedAll }));
  }, [data]);

  const dirty = snapshot !== JSON.stringify({ seed: draft, mutedAll });

  const setChannel = (eventKey, channel, value) =>
    setDraft((prev) => ({ ...prev, [eventKey]: { ...prev[eventKey], [channel]: value } }));

  const groups = useMemo(() => {
    const byDomain = {};
    (data?.events || []).forEach((ev) => {
      const d = ev.domain || "other";
      if (!byDomain[d]) byDomain[d] = [];
      byDomain[d].push(ev);
    });
    return Object.entries(byDomain);
  }, [data]);

  const channelValue = (ev, channel) => {
    const chDef = (ev.channels || []).find((c) => c.channel === channel);
    if (!chDef) return null;
    const override = draft[ev.eventKey]?.[channel];
    return override != null ? override : !!chDef.default;
  };

  const save = () => {
    updatePrefs.mutate(
      { preferences: draft, mutedAll },
      {
        onSuccess: () => {
          toast({ status: "success", title: "Notification preferences saved" });
          setSnapshot(JSON.stringify({ seed: draft, mutedAll }));
        },
        onError: (err) =>
          toast({
            status: "destructive",
            title: "Failed to save preferences",
            description: err?.response?.data?.error || "Unknown error",
          }),
      }
    );
  };

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle="Choose which events notify you, and how. Email and in-app are per-user; Telegram / Slack are company-wide broadcasts, managed separately."
        actions={
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={!dirty || updatePrefs.isLoading || isLoading}
          >
            {updatePrefs.isLoading ? "Saving…" : "Save changes"}
          </button>
        }
      />

      <div className="container space-y-6 py-6">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton.Rect className="h-16 w-full" />
            <Skeleton.Rect className="h-48 w-full" />
            <Skeleton.Rect className="h-40 w-full" />
          </div>
        ) : isError ? (
          <div className="surface p-8 text-center text-destructive">
            Failed to load notification preferences.
          </div>
        ) : (
          <>
            {/* Mute all */}
            <div className="surface flex items-center justify-between gap-4 rounded-xl p-4 ring-1 ring-border">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                  {mutedAll ? <BellOff className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
                </span>
                <div>
                  <div className="text-sm font-semibold text-foreground">Mute all notifications</div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Pause every email and in-app notification. Overrides the per-event settings below.
                  </p>
                </div>
              </div>
              <Switch checked={mutedAll} onChange={setMutedAll} label="Mute all notifications" />
            </div>

            {groups.length === 0 ? (
              <EmptyState
                icon={Bell}
                heading="No configurable notifications"
                description="There are no per-user notification events for your account yet."
              />
            ) : (
              <div className={`space-y-6 ${mutedAll ? "opacity-60" : ""}`}>
                {groups.map(([domain, events]) => (
                  <section key={domain} className="surface overflow-hidden rounded-xl ring-1 ring-border">
                    <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                      <h3 className="text-sm font-semibold capitalize text-foreground">{domain}</h3>
                      <div className="flex items-center gap-6 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {CHANNEL_ORDER.map((c) => (
                          <span key={c} className="w-12 text-center">
                            {CHANNEL_LABELS[c]}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="divide-y divide-border">
                      {events.map((ev) => (
                        <div key={ev.eventKey} className="flex items-center gap-4 px-4 py-3">
                          <div className="min-w-0 flex-1 text-sm text-foreground">{ev.label}</div>
                          <div className="flex items-center gap-6">
                            {CHANNEL_ORDER.map((c) => {
                              const val = channelValue(ev, c);
                              return (
                                <div key={c} className="flex w-12 justify-center">
                                  {val === null ? (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  ) : (
                                    <Switch
                                      checked={!!val}
                                      disabled={mutedAll}
                                      onChange={(v) => setChannel(ev.eventKey, c, v)}
                                      label={`${ev.label} — ${CHANNEL_LABELS[c]}`}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default requireAuth(NotificationPreferences);
